import * as THREE from "three";
import { getEdgeNormal } from "./getEdgeNormal";
import { intersectLines } from "./intersectLines";
import type { HelperLine } from "./types";

const DEGENERATE_EPSILON = 1e-10;

/**
 * Slides the given edges along their normals by `distance`, keeping every vertex on the infinite
 * lines of the edges that meet there. Edges that are not listed stay where they are; the vertices
 * they share with a moved edge simply slide along them, so the shape stays connected.
 *
 * All the selected lines move at once and the vertices are solved afterwards — offsetting one edge
 * at a time would make the result depend on the order, and two adjacent selected edges share a
 * vertex that is constrained by both of them.
 *
 * Returns null when a vertex cannot be constructed, i.e. the two lines meeting there are parallel.
 * Callers keep the previous geometry in that case.
 */
function offsetPolylineEdges({
	points,
	edgeIndices,
	distance,
	isLoop,
}: {
	points: ReadonlyArray<THREE.Vector3>;
	edgeIndices: ReadonlyArray<number>;
	distance: number;
	isLoop: boolean;
}): Array<THREE.Vector3> | null {
	const pointCount = points.length;
	const edgeCount = isLoop ? pointCount : pointCount - 1;

	if (edgeCount < 1) {
		return null;
	}

	const movedEdges = new Set(edgeIndices);
	const edgeLines: Array<HelperLine> = [];

	for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
		const start = points[edgeIndex];
		const end = points[(edgeIndex + 1) % pointCount];
		const direction = new THREE.Vector3(end.x - start.x, 0, end.z - start.z);

		if (direction.lengthSq() < DEGENERATE_EPSILON) {
			return null;
		}

		if (movedEdges.has(edgeIndex) === false) {
			edgeLines.push({ point: start.clone(), direction });
			continue;
		}

		const normal = getEdgeNormal({ points, edgeIndex, isLoop });

		if (normal === null) {
			return null;
		}

		edgeLines.push({ point: start.clone().addScaledVector(normal, distance), direction });
	}

	const result: Array<THREE.Vector3> = [];

	for (let pointIndex = 0; pointIndex < pointCount; pointIndex++) {
		// On a loop every vertex has both; on an open chain the two ends have only one.
		const incomingIndex = isLoop ? (pointIndex - 1 + edgeCount) % edgeCount : pointIndex - 1;
		const outgoingIndex = pointIndex < edgeCount ? pointIndex : -1;

		const incoming = incomingIndex >= 0 ? edgeLines[incomingIndex] : null;
		const outgoing = outgoingIndex >= 0 ? edgeLines[outgoingIndex] : null;

		const isAnyNeighbourMoved = (incomingIndex >= 0 && movedEdges.has(incomingIndex)) || (outgoingIndex >= 0 && movedEdges.has(outgoingIndex));

		// Solving a vertex whose lines both stayed put would return the same point, but recomputing it
		// on every keystroke would let rounding creep in. Leave it exactly as it was.
		if (isAnyNeighbourMoved === false) {
			result.push(points[pointIndex].clone());
			continue;
		}

		if (incoming !== null && outgoing !== null) {
			const corner = intersectLines({ first: incoming, second: outgoing });

			if (corner === null) {
				return null;
			}

			result.push(corner.setY(points[pointIndex].y));
			continue;
		}

		// An open end has a single edge and no corner to solve, so it travels with that edge. The
		// line's anchor was built from the edge's start vertex, so the difference is the offset itself.
		const line = incoming ?? outgoing;
		const anchorIndex = incoming !== null ? pointIndex - 1 : pointIndex;

		if (line === null) {
			return null;
		}

		result.push(points[pointIndex].clone().add(line.point.clone().sub(points[anchorIndex])));
	}

	return result;
}

export { offsetPolylineEdges };
